import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "../../styles/WalletRecharge.module.css";
import api,{getPublicKey} from "../../api/api";
import { openRazorpayPayment, generateFallbackPaymentInstructions, detectInAppBrowser, checkUPIAppAvailability } from "../../utils/razorpayUtils";
import { runCashfreeCheckout } from "../../checkout/strategies/CashfreeCheckout";
import { fetchCashfreeEligiblePaymentMethods, CASHFREE_CARD_ENABLED } from "../../utils/cashfreeUtils";
import CardSection from "../../components/checkout/CardSection";
import NetbankingSection from "../../components/checkout/NetbankingSection";
import WalletSection from "../../components/checkout/WalletSection";
import UpiIntentSection from "../../components/checkout/UpiIntentSection";
import FallbackPaymentModal from "../../components/FallbackPaymentModal";

const quickAddValues = [100, 200, 500, 1000, 2000];

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

const WalletRecharge = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Mock: Replace with API call to get real balance
  const [balance, setBalance] = useState(0); // Example starting balance
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const userId = JSON.parse(localStorage.getItem("user"))?.userid;
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(true);

  // Cashfree pure-custom-checkout state — order awaiting a method choice
  // (Razorpay's own hosted modal picks a method itself; Cashfree has no
  // such modal, so this page needs its own small method picker — reuses
  // the same CardSection/NetbankingSection/WalletSection/UpiIntentSection
  // components CheckoutPage.jsx already uses, all gateway-agnostic).
  const [cfOrder, setCfOrder] = useState(null);
  const [cfMethodTab, setCfMethodTab] = useState('upi');
  // Live netbanking/wallet eligibility (2026-09-17) — this page isn't
  // wrapped in CheckoutProvider (CheckoutContext.jsx), so it fetches this
  // independently rather than sharing that context's fetch. See
  // cashfreeUtils.js::fetchCashfreeEligiblePaymentMethods's doc comment for
  // why this replaces CASHFREE_POPULAR_BANKS/CASHFREE_POPULAR_WALLETS as
  // the source of truth.
  const [cashfreePaymentMethods, setCashfreePaymentMethods] = useState({ netbanking: [], wallet: [] });
  const [cashfreeMethodsLoaded, setCashfreeMethodsLoaded] = useState(false);
  // Real per-app UPI targeting (GPay/PhonePe/Paytm) + the "awaiting payment"
  // screen — this page's UPI tab was still bare UpiCollectSection (VPA-only),
  // out of sync with CheckoutPage.jsx's full UpiIntentSection experience
  // (found live, 2026-09-13). deviceInfo mirrors CheckoutPage.jsx's own
  // isAndroid/isIOS detection, needed by UpiIntentSection to pick its
  // mobile-grid vs desktop-QR layout.
  const deviceInfo = useMemo(() => checkUPIAppAvailability(), []);
  const [openedUpiApp, setOpenedUpiApp] = useState(null);

  // Enhanced payment states
  const [fallbackModal, setFallbackModal] = useState({
    show: false,
    amount: 0,
    orderId: null,
    instructions: null,
  });
  const [browserWarning, setBrowserWarning] = useState(null);

  const fetchBalance = async () => {
    setBalanceLoading(true);
    try {
      const response = await api.get(`/wallet/balance/${userId}`);
      if (
        response.status === 200 &&
        response.data.wallet_balance !== undefined
      ) {
        setBalance(response.data.wallet_balance);
      } else {
        console.warn("Unexpected API response:", response);
      }
    } catch (err) {
      console.error("Failed to fetch wallet balance:", err);
      setError("Failed to load wallet balance. Please refresh the page.");
    } finally {
      setBalanceLoading(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get(`/wallet/recharge-histories/${userId}`);
      if (res.data && Array.isArray(res.data.recharge_histories)) {
        setHistory(res.data.recharge_histories);
      } else {
        setHistory([]);
      }
    } catch (err) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchBalance();
    fetchHistory();

    // Check browser compatibility on component mount
    const browserCheck = detectInAppBrowser();
    if (browserCheck.isInAppBrowser) {
      setBrowserWarning(browserCheck.recommendation);
    }
  }, [userId]);

  // Shared poller — used both right after a payment attempt succeeds AND when
  // resuming after a Cashfree redirect (see the cashfree_order_id effect
  // below). `gatewayOrderId` works for either gateway — check-status now
  // checks both razorpay_order_id and cashfree_order_id (backend fix).
  const pollWalletRechargeStatus = (gatewayOrderId) => {
    const checkInterval = setInterval(async () => {
      try {
        const res = await api.get(`/wallet/check-status/${gatewayOrderId}`);
        const status = res.data.status;

        if (status === "success") {
          clearInterval(checkInterval);
          setLoading(false);
          setSuccess("🎉 Wallet recharged successfully!");
          fetchBalance();
          fetchHistory();
          setAmount("");
          setCfOrder(null);
        } else if (status === "failed") {
          clearInterval(checkInterval);
          setLoading(false);
          setError("Payment failed. Please try again.");
        }
        // if status is still 'pending', continue polling
      } catch (err) {
        console.error("Error checking recharge status:", err);
        clearInterval(checkInterval);
        setLoading(false);
        setError("Error verifying payment. Please contact support.");
      }
    }, 2000); // Check every 2 seconds

    // Safety timeout - stop polling after 1 minute
    setTimeout(() => {
      clearInterval(checkInterval);
      if (loading) {
        setLoading(false);
        setError("Recharge verification is taking longer than expected. Please refresh and check your wallet balance.");
      }
    }, 60000);
  };

  // Resume polling after a Cashfree redirect (Card/Netbanking 3DS/bank-auth
  // sends the browser back here instead of leaving it mid-payment) — see
  // upiPaymentController.js::cashfreeWalletCallback on the backend. A pure
  // wallet top-up has no AstroShubh order, so this page (not /checkout-callback)
  // is the return target.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cashfreeOrderId = params.get('cashfree_order_id');
    if (!cashfreeOrderId) return;

    setError("");
    setLoading(true);
    pollWalletRechargeStatus(cashfreeOrderId);

    // Strip the query param so a page refresh doesn't re-poll a
    // long-since-resolved (or expired) payment attempt.
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRecharge = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Input validation
    const numAmount = Number(amount);
    if (!amount || isNaN(numAmount) || numAmount < 10) {
      setError("Please enter a valid amount (minimum ₹10)");
      return;
    }
    if (numAmount > 50000) {
      setError("Maximum recharge amount is ₹50,000 per transaction");
      return;
    }

    setLoading(true);
    try {
      // Call backend to initiate Razorpay order
      const { data } = await api.post("/wallet/initiate", {
        userId,
        amount: numAmount,
      });

      if (!data.order) {
        throw new Error("Order creation failed");
      }

      if (data.order.gateway === 'cashfree') {
        // No Razorpay-style hosted modal for Cashfree's pure custom checkout
        // — show our own small method picker instead of paying immediately
        // (see the cfOrder-driven render block below).
        setCfOrder(data.order);
        setCfMethodTab('upi');
        setLoading(false);
        fetchCashfreeEligiblePaymentMethods(data.order.amount / 100).then((methods) => {
          setCashfreePaymentMethods(methods);
          setCashfreeMethodsLoaded(true);
        });
        return;
      }

      // ─── Razorpay path below — unchanged ───
      // Get Razorpay key from public config
      const razorpayKey = await getPublicKey('RAZORPAY_KEY_ID');

      // Get user data for prefill
      const userData = JSON.parse(localStorage.getItem("user")) || {};

      // Use enhanced Razorpay integration
      await openRazorpayPayment({
        razorpayKey,
        order: data.order,
        customerName: userData.name || "",
        // Bug fix (found live on STG, 2026-09-13, while chasing a Cashfree
        // "missing phone" error below): the stored user object's field is
        // `mobile`, not `phone` (confirmed against every other place in the
        // app that reads it — UserProfile.jsx, the backend's own
        // create-order-after-otp response). This was silently sending an
        // empty contact to Razorpay's prefill the whole time — harmless
        // there since Razorpay doesn't require it, but same wrong field name.
        customerContact: userData.mobile || "",
        notes: {
          wallet_recharge: true,
          user_id: userId,
          amount: numAmount
        },
        onSuccess: async (response) => {
          console.log("✅ Wallet recharge payment success, checking status...", {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
          });
          pollWalletRechargeStatus(response.razorpay_order_id);
        },
        onFailure: (response) => {
          console.error("❌ Wallet recharge payment failed:", response);
          setLoading(false);
          
          // Handle different types of failures
          if (response.error?.code === "PAYMENT_CANCELLED") {
            setError("Payment was cancelled. You can try again whenever you're ready.");
          } else if (response.error?.code === "NETWORK_ERROR") {
            // Show fallback payment options for network issues
            const instructions = generateFallbackPaymentInstructions(numAmount, data.order.id);
            setFallbackModal({
              show: true,
              amount: numAmount,
              orderId: data.order.id,
              instructions
            });
          } else {
            // For UPI app issues or other failures, show fallback
            const instructions = generateFallbackPaymentInstructions(numAmount, data.order.id);
            setFallbackModal({
              show: true,
              amount: numAmount,
              orderId: data.order.id,
              instructions
            });
          }
        }
      });
      
    } catch (err) {
      console.error("💥 Error in wallet recharge:", err);
      setLoading(false);
      setError("Unable to process payment. Please try again later.");
      setSuccess("");
    }
  };

  // Cashfree pure-custom-checkout submission — called by whichever method
  // component (CardSection/NetbankingSection/WalletSection/UpiIntentSection)
  // is active in cfMethodTab, same {method, ...details} shape they already
  // call onPay with for CheckoutPage.jsx.
  const handleCashfreePay = async ({ method, app, ...details }) => {
    if (!cfOrder) return;
    setError("");
    setLoading(true);
    if (method === 'upi-intent') setOpenedUpiApp(app);
    // Required for method:'wallet'/'upi-intent' — Cashfree's
    // payment_method.app.phone is mandatory (see
    // cashfreeUtils.js::buildCashfreePaymentMethod's doc comment).
    //
    // Bug fix (found live on STG, 2026-09-13): this read userData.phone,
    // but the stored user object's field is `mobile` (see the Razorpay
    // branch's matching fix above) — always empty, which is exactly what
    // surfaced the "Missing customer phone number" error live for the
    // first time (Razorpay's own field never enforced it, so this same
    // wrong name went unnoticed there).
    const userData = JSON.parse(localStorage.getItem("user")) || {};
    await runCashfreeCheckout({
      order: cfOrder,
      method,
      app,
      details,
      customerPhone: userData.mobile || "",
      onSuccess: () => {
        // "custom" action (e.g. UPI collect) — no redirect, poll immediately.
        // A "link" action navigates the browser away instead (see
        // CashfreeCheckout.js) and comes back via cashfreeWalletCallback's
        // redirect + the resume-on-mount effect above.
        setOpenedUpiApp(null);
        pollWalletRechargeStatus(cfOrder.id);
      },
      onFailure: (resp) => {
        setLoading(false);
        setOpenedUpiApp(null);
        setError(resp?.error?.description || "Payment failed. Please try again.");
      },
    });
  };

  const handleQuickAdd = (val) => {
    const currentAmount = Number(amount) || 0;
    const newAmount = currentAmount + val;
    if (newAmount <= 50000) {
      setAmount(String(newAmount));
      setError(""); // Clear any existing errors
    } else {
      setError("Maximum recharge amount is ₹50,000 per transaction");
    }
  };

  const clearAmount = () => {
    setAmount("");
    setError("");
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className={styles.modernWalletPage}>
      <div className={styles.modernWalletContainer}>
        {/* Header Section */}
        <div className={styles.modernWalletHeader}>
          <div className={styles.walletHeaderContent}>
            <h1 className={styles.modernWalletTitle}>
              <span className={styles.walletIcon}>💰</span>
              Wallet Recharge
            </h1>
            <p className={styles.modernWalletSubtitle}>
              Add funds to your wallet for seamless consultations
            </p>
          </div>
        </div>

        <div className={styles.modernWalletContent}>
          {/* Balance Card */}
          <div className={styles.balanceCard}>
            <div className={styles.balanceHeader}>
              <h3 className={styles.balanceTitle}>Current Balance</h3>
              {balanceLoading ? (
                <div className={styles.balanceLoader}>
                  <div className={styles.spinnerSmall}></div>
                </div>
              ) : (
                <button 
                  className={styles.refreshBtn} 
                  onClick={fetchBalance}
                  disabled={balanceLoading}
                  title="Refresh Balance"
                >
                  🔄
                </button>
              )}
            </div>
            <div className={styles.balanceAmount}>
              {balanceLoading ? (
                <span className={styles.balanceSkeleton}>Loading...</span>
              ) : (
                <span className={styles.balanceValue}>{formatCurrency(balance)}</span>
              )}
            </div>
          </div>

          {/* Recharge Form — replaced by a method picker once a Cashfree
              order is pending payment-method selection (see cfOrder) */}
          {cfOrder ? (
            <div className={styles.rechargeCard}>
              <h3 className={styles.rechargeTitle}>Choose Payment Method</h3>

              <div className={styles.quickAmountsGrid}>
                {[
                  { id: 'upi', label: 'UPI' },
                  { id: 'card', label: 'Card' },
                  { id: 'netbanking', label: 'Netbanking' },
                  { id: 'wallet', label: 'Wallet' },
                ].filter((tab) => tab.id !== 'card' || CASHFREE_CARD_ENABLED).map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={styles.modernQuickBtn}
                    onClick={() => { setCfMethodTab(tab.id); setError(""); }}
                    disabled={loading}
                    style={cfMethodTab === tab.id ? { borderColor: '#F6C600', borderWidth: 2 } : undefined}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div style={{ marginTop: 16 }}>
                {cfMethodTab === 'upi' && (
                  <UpiIntentSection
                    gateway="cashfree"
                    isAndroid={deviceInfo.isAndroid}
                    isIOS={deviceInfo.isIOS}
                    isInApp={false}
                    processing={loading}
                    openedApp={openedUpiApp}
                    amount={cfOrder.amount / 100}
                    onPay={handleCashfreePay}
                    onChangeApp={() => setOpenedUpiApp(null)}
                    liveWallets={cashfreePaymentMethods.wallet}
                  />
                )}
                {cfMethodTab === 'card' && (
                  <CardSection amount={cfOrder.amount / 100} processing={loading} onPay={handleCashfreePay} />
                )}
                {cfMethodTab === 'netbanking' && (
                  <NetbankingSection amount={cfOrder.amount / 100} processing={loading} onPay={handleCashfreePay} gateway="cashfree" liveBanks={cashfreePaymentMethods.netbanking} liveBanksLoaded={cashfreeMethodsLoaded} />
                )}
                {cfMethodTab === 'wallet' && (
                  <WalletSection amount={cfOrder.amount / 100} processing={loading} onPay={handleCashfreePay} gateway="cashfree" liveWallets={cashfreePaymentMethods.wallet} liveWalletsLoaded={cashfreeMethodsLoaded} />
                )}
              </div>

              {error && (
                <div className={`${styles.modernMessage} ${styles.errorMessage}`}>
                  <span className={styles.messageIcon}>⚠️</span>
                  {error}
                </div>
              )}

              <button
                type="button"
                onClick={() => { setCfOrder(null); setError(""); }}
                disabled={loading}
                style={{ marginTop: 12, width: '100%', background: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: 8, padding: '10px 0', cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                ← Cancel, choose a different amount
              </button>

              <div className={styles.securityInfo}>
                <div className={styles.securityBadge}>
                  <span className={styles.securityIcon}>🔒</span>
                  <span className={styles.securityText}>Secured by Cashfree • All transactions are encrypted</span>
                </div>
              </div>
            </div>
          ) : (
          <div className={styles.rechargeCard}>
            <h3 className={styles.rechargeTitle}>Add Money</h3>

            <form onSubmit={handleRecharge} className={styles.modernRechargeForm}>
              {/* Amount Input */}
              <div className={styles.amountInputSection}>
                <label className={styles.amountLabel}>Enter Amount</label>
                <div className={styles.amountInputWrapper}>
                  <span className={styles.currencySymbol}>₹</span>
                  <input
                    type="number"
                    min="10"
                    max="50000"
                    value={amount}
                    onChange={(e) => {
                      setAmount(e.target.value);
                      setError("");
                    }}
                    placeholder="0"
                    className={styles.modernAmountInput}
                    disabled={loading}
                  />
                  {amount && (
                    <button
                      type="button"
                      className={styles.clearAmountBtn}
                      onClick={clearAmount}
                      disabled={loading}
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className={styles.amountLimits}>
                  Min: ₹10 • Max: ₹50,000
                </div>
              </div>

              {/* Quick Add Buttons */}
              <div className={styles.quickAmountsSection}>
                <label className={styles.quickAmountsLabel}>Quick Add</label>
                <div className={styles.quickAmountsGrid}>
                  {quickAddValues.map((val) => (
                    <button
                      type="button"
                      key={val}
                      className={styles.modernQuickBtn}
                      onClick={() => handleQuickAdd(val)}
                      disabled={loading}
                    >
                      +₹{val.toLocaleString('en-IN')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Messages */}
              {error && (
                <div className={`${styles.modernMessage} ${styles.errorMessage}`}>
                  <span className={styles.messageIcon}>⚠️</span>
                  {error}
                </div>
              )}
              {success && (
                <div className={`${styles.modernMessage} ${styles.successMessage}`}>
                  <span className={styles.messageIcon}>✅</span>
                  {success}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className={styles.modernRechargeBtn}
                disabled={loading || !amount || Number(amount) < 10}
              >
                {loading ? (
                  <>
                    <div className={styles.btnSpinner}></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <span className={styles.btnIcon}>💳</span>
                    Recharge {amount ? formatCurrency(Number(amount)) : 'Wallet'}
                  </>
                )}
              </button>
            </form>

            {/* Security Info */}
            <div className={styles.securityInfo}>
              <div className={styles.securityBadge}>
                <span className={styles.securityIcon}>🔒</span>
                <span className={styles.securityText}>
                  Secured &amp; encrypted • All transactions are protected
                </span>
              </div>
            </div>
          </div>
          )}

          {/* Transaction History */}
          <div className={styles.historyCard}>
            <div className={styles.historyHeader}>
              <h3 className={styles.historyTitle}>
                <span className={styles.historyIcon}>📋</span>
                Wallet Recharge History
              </h3>
              <button 
                className={styles.refreshHistoryBtn} 
                onClick={fetchHistory}
                disabled={historyLoading}
                title="Refresh History"
              >
                🔄
              </button>
            </div>
            
            <div className={styles.historyContent}>
              {historyLoading ? (
                <div className={styles.historyLoading}>
                  <div className={styles.spinner}></div>
                  <span>Loading recharge  history...</span>
                </div>
              ) : history.length === 0 ? (
                <div className={styles.historyEmpty}>
                  <div className={styles.emptyIcon}>📝</div>
                  <h4>No transactions yet</h4>
                  <p>Your recharge history will appear here</p>
                </div>
              ) : (
                <div className={styles.historyList}>
                  {history.map((item, idx) => {
                    // Backend now returns both id columns + a gateway
                    // discriminator (models/walletRechargeModel.js fix) — show
                    // whichever one is actually populated for this row.
                    const gatewayOrderId = item.gateway === 'cashfree' ? item.cashfree_order_id : item.razorpay_order_id;
                    return (
                    <div className={styles.modernHistoryItem} key={gatewayOrderId + idx}>
                      <div className={styles.historyItemMain}>
                        <div className={styles.transactionInfo}>
                          <div className={styles.transactionAmount}>
                            {formatCurrency(item.amount)}
                          </div>
                          <div className={styles.transactionDate}>
                            {formatDate(item.created_at)}
                          </div>
                        </div>
                        <div className={`${styles.transactionStatus} ${styles[`status${item.status.charAt(0).toUpperCase() + item.status.slice(1)}`]}`}>
                          <span className={styles.statusDot}></span>
                          {item.status}
                        </div>
                      </div>
                      <div className={styles.historyItemMeta}>
                        <div className={styles.transactionMethod}>
                          Method: {item.method}
                        </div>
                        <div className={styles.transactionId}>
                          ID: {gatewayOrderId}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Enhanced Fallback Payment Modal */}
        <FallbackPaymentModal
          show={fallbackModal.show}
          amount={fallbackModal.amount}
          orderId={fallbackModal.orderId}
          instructions={fallbackModal.instructions}
          onClose={() => setFallbackModal({ show: false, amount: 0, orderId: null, instructions: null })}
          onRetry={() => {
            setFallbackModal({ show: false, amount: 0, orderId: null, instructions: null });
            handleRecharge({ preventDefault: () => {} }); // Retry payment
          }}
        />
      </div>
    </div>
  );
};

export default WalletRecharge;
